import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ChatAIForm.scss';
import { ChatAIAvatar } from '../ChatAIAvatar/ChatAIAvatar';
import { ChatAIDaySeparator } from '../ChatAIDaySeparator/ChatAIDaySeparator';
import { ChatAIFeedEntry } from '../ChatAIFeedEntry/ChatAIFeedEntry';
import { ChatAIMessageInput, type ChatAIMessageInputHandle } from '../ChatAIMessageInput/ChatAIMessageInput';
import { ChatAIMessageList } from '../ChatAIMessageList/ChatAIMessageList';
import Modal from '@srm/ui/modal';
import { formatDaySeparator, formatMessageTime } from '@/utils/chatDateTime';
import { ChatAIService } from '@/services/ChatAIService';
import { ChatAIHistoryService } from '@/services/ChatAIHistoryService';
import { IChatAIQuestion } from '@/models/chatai/IChatAIQuestion';
import { IChatAIRAGResponse } from '@/models/chatai/IChatAIRAGResponse';
import { chatAIHub } from '@/signalr/hubs/ChatAIHub';
import { MdClose, MdMenu } from 'react-icons/md';
import { TbPictureInPictureOn } from 'react-icons/tb';
import { ConfigService } from '@/configuration/services/configService';
import { v4 as uuidv4 } from 'uuid';
import type { ChatAIFormMessage } from './ChatAIFormMessage';
import { withDaySeparators } from './chatFeed';
import { buildAttachmentDownloadHref, stripAccessTokens } from './chatAttachmentLinks';
import ChatAITypingStatus from './ChatAITypingStatus';
import ChatAIMiniature from '../Miniature/ChatAIMiniature';
import {
    STATUS_BADGE_TEXTS,
    STATUS_MINI_TEXTS,
    STATUS_OFFLINE_HINT,
    STATUS_RECONNECTING_HINT,
    getOfflineHint,
} from '../constants';
import { ChatAIIndicatorStatus } from '@/hooks/useChatAIStatus';
import { useChatAIHistoryEnabled } from '@/hooks/useChatAIHistoryEnabled';
import { useTypedSelector } from '@/hooks/useTypedSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import {
    addLocalMessage,
    markCurrentPersisted,
    messagesLoadSuccess,
    setMode,
    startNewConversation,
    toggleSidebarMobile,
    updateLocalMessage,
} from '@/store/reducers/ChatAIHistoryReducer';
import { ChatAISidebar } from '../Sidebar/ChatAISidebar';
import { ChatAIModeSelector } from './ChatAIModeSelector';
import { IConversationMessage } from '@/models/chatai/IConversationMessage';

import axios, { AxiosResponse } from 'axios';
import { IChatAIAnswer } from '@models/chatai/IChatAIAnswer';

export interface ChatAIFormProps {
    onClose: (e: unknown) => void;
    onMinimize: () => void;
    onExpand: () => void;
    isMinimized: boolean;
    status: ChatAIIndicatorStatus;
    notConfigured: boolean;
}

const ENABLE_MOCK_BOT_RESPONSE = false;
const DEFAULT_INITIAL_QUESTIONS_DISPLAY_LIMIT = 5;
const PENDING_TTL_MS = 5 * 60 * 1000;
const AGENT_STATUS_POLL_INTERVAL_MS = 2 * 1000;
const AGENT_STATUS_POLL_MAX_MS = 5 * 60 * 1000;
const AGENT_FINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'approval_pending']);
/** Маркер hero-сообщения (заглавный экран без MessageList). */
const HELLO_MESSAGE_ID = '-1';
const DEFAULT_HELLO_TEXT = 'Добрый день!\n\nЯ — ваш цифровой помощник. Вы можете задать мне вопрос, касающийся нашей системы, и я постараюсь вам помочь.';

function nowTime(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Отметка текущего момента: короткое время для футера + ISO для разделителей/тултипа. */
function nowStamp(): { sentTime: string; sentAt: string } {
    const iso = new Date().toISOString();
    return { sentTime: formatMessageTime(iso), sentAt: iso };
}

function isAgentFinalStatus(status?: string | null): boolean {
    return status ? AGENT_FINAL_STATUSES.has(status.toLowerCase()) : false;
}

function isConversationMessageFinal(m: IConversationMessage): boolean {
    return m.status !== 'pending' || Boolean(m.answer);
}

/**
 * Маппит persisted-сообщение из бэк-истории в пару (user, assistant) ChatAIFormMessage.
 * Всегда возвращает обе части, даже для зависшего pending: иначе последнее сообщение
 * становилось бы outgoing и useEffect[messages] (см. ниже) посчитал бы его свежим
 * вводом и снова запустил processMessages — дублируя запрос при простом открытии
 * старого незавершённого треда.
 * Для answer=null + error/cancelled/permission_denied/not_found рендерим компактную плашку;
 * pending старше 5 мин получает явный «зависший» текст вместо «формируется…».
 */
function conversationMessageToFormPair(m: IConversationMessage, assistantName: string): ChatAIFormMessage[] {
    const created = new Date(m.createdAt);
    const isValidCreated = !isNaN(created.getTime());
    const time = isValidCreated ? formatMessageTime(m.createdAt) : '';
    const sentAt = isValidCreated ? m.createdAt : undefined;

    const userMessage: ChatAIFormMessage = {
        message: m.question,
        direction: 'outgoing',
        sender: 'user',
        sentTime: time,
        sentAt,
        id: `q-${m.id}`,
    };

    const isStalePending = m.status === 'pending' && Date.now() - created.getTime() > PENDING_TTL_MS;

    let answerText: string | null = m.answer;
    if (!answerText) {
        switch (m.status) {
            case 'error':
                answerText = 'Не удалось получить ответ. Повторите попытку позже.';
                break;
            case 'cancelled':
                answerText = 'Запрос отменён.';
                break;
            case 'permission_denied':
                answerText = 'Нет доступа к контентной базе знаний.';
                break;
            case 'not_found':
                answerText = 'По вашему запросу ничего не найдено.';
                break;
            case 'pending':
                answerText = isStalePending
                    ? 'Ответ так и не пришёл. Задайте вопрос ещё раз.'
                    : 'Ответ всё ещё формируется…';
                break;
            default:
                answerText = '';
        }
    }

    const assistantMessage: ChatAIFormMessage = {
        message: stripAccessTokens(answerText),
        direction: 'incoming',
        sender: assistantName,
        sentTime: time,
        sentAt,
        id: m.requestId || `a-${m.id}`,
        isError: m.status === 'error',
    };

    return [userMessage, assistantMessage];
}

const pickRandomQuestions = (questions: string[], count: number): string[] => {
    if (questions.length <= count) {
        return questions;
    }

    const shuffledQuestions = [...questions];

    for (let i = shuffledQuestions.length - 1; i > 0; i--) {
        const randomIndex = Math.floor(Math.random() * (i + 1));
        [shuffledQuestions[i], shuffledQuestions[randomIndex]] = [shuffledQuestions[randomIndex], shuffledQuestions[i]];
    }

    return shuffledQuestions.slice(0, count);
};

const MOCK_BOT_MARKDOWN_RESPONSE = `# H1. Основной заголовок
## H2. Подзаголовок раздела
### H3. Подраздел
#### H4. Уровень 4
##### H5. Уровень 5
###### H6. Уровень 6

Обычный абзац с **жирным**, *курсивом*, ***жирным курсивом***, ~~зачеркнутым~~ и \`inline code\`.

---

Ссылка: [Документация](https://example.com) и авто-ссылка https://example.org

![Пример изображения](https://picsum.photos/480/180)

> Цитата первого уровня.
>
> - Пункт внутри цитаты
> - Еще один пункт
>
> \`код в цитате\`

- Ненумерованный список
- Пункт со вложенным списком
  - Вложенный пункт 1
  - Вложенный пункт 2
    - Третий уровень
- [x] Выполненная задача
- [ ] Невыполненная задача

1. Нумерованный список
2. Второй пункт
3. Третий пункт
   1. Вложенная нумерация
   2. Еще пункт

| Поле | Значение | Комментарий |
| --- | --- | --- |
| Статус | OK | Таблица в GFM |
| Версия | 1.0 | Проверка границ |
| Дата | 2026-03-05 | Проверка ширины |

\`\`\`ts
type User = { id: number; name: string };

const users: User[] = [
    { id: 1, name: 'Anna' },
    { id: 2, name: 'Ivan' },
];

const active = users.filter((u) => u.id > 1);
console.log(active);
\`\`\`

\`\`\`
Кодовый блок без языка
строка 2
\`\`\`


# Как обработать аналоги по закупочной процедуре?

Обработка аналогов — важный этап при оценке заявок в закупочных процедурах. Это позволяет учесть альтернативные предложения, которые не полностью соответствуют исходным требованиям, но могут быть приемлемы для заказчика. Ниже пошаговая инструкция:

## Пошаговая инструкция по обработке аналогов

1. **Перейдите в раздел \\"Аналоги\\"**  
   В интерфейсе закупочной процедуры найдите и откройте вкладку **«Аналоги»**. Здесь отображаются все позиции, по которым поступили альтернативные предложения.

2. **Выберите позицию для обработки**  
   В списке позиций укажите ту, по которой необходимо оценить аналог. Убедитесь, что выбрана именно та позиция, которая требует дополнительного рассмотрения.

3. **Инициируйте обработку аналога**  
   Нажмите кнопку **«Действия по позиции»** и в выпадающем меню выберите **«Обработать аналог»**. Это откроет форму для заполнения данных об альтернативном предложении.

4. **Заполните сведения об аналоге**  
   В открывшейся форме укажите:
   - Детальное описание аналога
   - Основные характеристики (соответствие требованиям)
   - Причины, по которым этот аналог рассматривается
   - Другие релевантные данные

   После заполнения нажмите **«ОК»** для сохранения.

## Важные моменты

- **Связь с этапом выбора победителя**  
  Обработка аналогов происходит на этапе **оценки заявок**. После завершения оценки появится возможность перевести процедуру на этап согласования и создать протокол.

- **Завершение оценки**  
  После обработки всех аналогов необходимо **завершить оценку** (проставить место и выбрать основного поставщика). Только после этого станет доступна кнопка **«Создать протокол»**.

- **Технические особенности**  
  Если в разделе \\"Выбор победителя\\" отсутствует пункт \\"Оценка победителя\\", обратитесь к администратору системы — возможно, требуется активация соответствующего признака (как в процедуре 3545).

## Заключение

Правильная обработка аналогов обеспечивает прозрачность и конкурентность закупочного процесса. Следуя описанным шагам, вы сможете корректно оценить альтернативные предложения и принять обоснованное решение о выборе победителя.
`;

const ChatAIForm: React.FC<ChatAIFormProps> = ({
    onClose,
    onMinimize,
    onExpand,
    isMinimized,
    status,
    notConfigured,
}: ChatAIFormProps) => {
    const config = ConfigService.get();
    const chatAITitle = config.application.AI?.chatAITitle;
    const chatAIEnabled = config.application.AI?.chatAIEnabled !== false;
    const agentAIEnabled = config.application.AI?.agentAIEnabled === true;

    // Mode читается из slice. Default — localStorage (initial state в reducer).
    // Persisted-тред (выбран в sidebar) перезаписывает state.currentMode своим mode из БД.
    const dispatch = useAppDispatch();
    const currentMode = useTypedSelector((s) => s.chatAIHistory.currentMode);
    const useAIAgent = currentMode === 'agent';

    // Mode-specific конфиг: hello-text, typing-pool, initial-questions берём из AI[mode].
    const modeConfig = useAIAgent ? config.application.AI?.Agent : config.application.AI?.RAG;
    const chatAIHelloMessage = modeConfig?.chatAIHelloMessage;
    const typingStatusMessages = modeConfig?.typingStatusMessages;
    const chatAIInitialQuestionsDisplayCount = modeConfig?.chatAIInitialQuestionsDisplayCount;
    const allChatAIInitialQuestions = (modeConfig?.chatAIInitialQuestions ?? [])
        .map((question) => question.trim())
        .filter((question) => question.length > 0);
    const initialQuestionsDisplayLimit =
        typeof chatAIInitialQuestionsDisplayCount === 'number'
            ? Math.max(0, Math.floor(chatAIInitialQuestionsDisplayCount))
            : DEFAULT_INITIAL_QUESTIONS_DISPLAY_LIMIT;
    // Initial-questions пересчитываются при смене режима — иначе при переключении на hero
    // оставались бы вопросы предыдущего режима. Стабильный shuffle до следующей смены mode.
    // eslint-disable: allChatAIInitialQuestions/initialQuestionsDisplayLimit пересоздаются на
    // каждом render, но их содержимое стабильно через ConfigService.get() (синглтон) и зависит
    // только от currentMode. Если конфиг станет динамическим — здесь нужны будут реальные deps.
    const chatAIInitialQuestions = useMemo(
        () => pickRandomQuestions(allChatAIInitialQuestions, initialQuestionsDisplayLimit),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentMode],
    );
    const assistantName = chatAITitle || 'Цифровой помощник';

    const buildHelloMessage = useCallback((): ChatAIFormMessage => ({
        direction: 'incoming',
        message: chatAIHelloMessage || DEFAULT_HELLO_TEXT,
        sender: assistantName,
        sentTime: nowTime(),
        id: HELLO_MESSAGE_ID,
    }), [assistantName, chatAIHelloMessage]);

    const [messages, setMessages] = useState<ChatAIFormMessage[]>(() => [buildHelloMessage()]);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [completedReplyId, setCompletedReplyId] = useState<string>();
    const requestIdRef = useRef<string>('');
    const abortControllerRef = useRef<AbortController | null>(null);
    const conversationIdRef = useRef<string>(uuidv4());
    const inputRef = useRef<ChatAIMessageInputHandle>(null);
    const unsentMessage = useRef('');
    const [sentCount, setSentCount] = useState(0);
    /** ID локального pending-черновика в slice — нужен, чтобы пометить его error/cancelled,
     *  иначе draft остаётся в state навсегда (до переключения треда + getConversation refetch). */
    const pendingDraftIdRef = useRef<number | null>(null);
    const agentStatusPollTimerRef = useRef<number | null>(null);
    const agentStatusPollStartedAtRef = useRef<number>(0);

    // ---- История диалогов (slice) ----
    // Sidebar теперь общий для обоих режимов: треды содержат поле mode, badge на item.
    // Agent тоже пишется в БД (см. AgentAIService после Commit 2).
    const historyEnabled = useChatAIHistoryEnabled();
    const sliceCurrentId = useTypedSelector((s) => s.chatAIHistory.currentConversationId);
    const sliceIsCurrentPersisted = useTypedSelector((s) => s.chatAIHistory.isCurrentPersisted);
    const sliceMessages = useTypedSelector((s) =>
        sliceCurrentId ? s.chatAIHistory.messagesByConversation[sliceCurrentId] ?? null : null,
    );
    const sliceMessagesLoaded = useTypedSelector((s) =>
        sliceCurrentId ? s.chatAIHistory.messagesLoaded[sliceCurrentId] ?? false : false,
    );

    const clearAgentStatusPolling = useCallback(() => {
        if (agentStatusPollTimerRef.current != null) {
            window.clearTimeout(agentStatusPollTimerRef.current);
            agentStatusPollTimerRef.current = null;
        }
    }, []);

    const finishPendingRequest = useCallback(
        (requestId?: string) => {
            const id = requestId || requestIdRef.current;
            clearAgentStatusPolling();
            if (id) {
                chatAIHub.leaveGroup(id).catch(() => {});
            }
            if (!requestId || requestIdRef.current === requestId) {
                requestIdRef.current = '';
            }
            abortControllerRef.current = null;
            setIsTyping(false);
        },
        [clearAgentStatusPolling],
    );

    // При каждом mount формы (открытие виджета) — стартуем новый тред с hello,
    // даже если в slice остался предыдущий persisted thread от прошлой сессии.
    // Юзер при повторном открытии должен видеть приветствие, а не последний чат.
    // startNewConversation идемпотентен: если уже есть свежий не-persisted тред с пустыми
    // сообщениями — no-op, а не лишний uuid.
    useEffect(() => {
        if (!historyEnabled) return;
        dispatch(startNewConversation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyEnabled]);

    // Effect 1: abort + reset при смене активного треда. Не реагирует на silent refresh
    // (тот же sliceCurrentId, обновился только список messages) — иначе мы бы прерывали
    // own pending-запрос на каждом push'е от другого таба.
    useEffect(() => {
        if (!historyEnabled) return;

        // Смена треда / его удаление прерывает pending-ответ.
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        clearAgentStatusPolling();
        if (requestIdRef.current) {
            chatAIHub.leaveGroup(requestIdRef.current).catch(() => {});
            requestIdRef.current = '';
        }
        setIsTyping(false);

        if (!sliceCurrentId) {
            // Активного треда нет (удалили или не было). Сразу показываем hero
            // и заводим в slice свежий тред — это держит в синке slice.currentConversationId
            // с conversationIdRef (иначе markCurrentPersisted при ответе не сможет
            // optimistic-insert), а также даёт юзеру немедленно writeable hello-state.
            setMessages([buildHelloMessage()]);
            dispatch(startNewConversation());
            return;
        }

        conversationIdRef.current = sliceCurrentId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliceCurrentId, clearAgentStatusPolling]);

    // Effect 2: рендерим form.messages из slice — и при первом load после select,
    // и при silent refresh от conversationsUpdated push (другой таб ответил в тот же тред).
    // Guard pendingDraftIdRef: когда юзер только что нажал Send, у нас локально outgoing
    // message + pending draft в slice; силент-refresh от другого таба не должен перерисовать
    // form пока own ответ не дошёл (иначе draft перетрётся данными без него).
    useEffect(() => {
        if (!historyEnabled) return;
        if (!sliceCurrentId) return;
        if (!sliceMessagesLoaded) return;
        if (!sliceMessages) return;
        if (pendingDraftIdRef.current != null) return;

        if (sliceMessages.length > 0) {
            const mapped: ChatAIFormMessage[] = sliceMessages.flatMap((m) =>
                conversationMessageToFormPair(m, assistantName),
            );
            setMessages(mapped);
        } else {
            setMessages([buildHelloMessage()]);
        }

        const pendingRequestId = requestIdRef.current;
        const finalMessage = pendingRequestId
            ? sliceMessages.find((m) => m.requestId === pendingRequestId && isConversationMessageFinal(m))
            : null;
        if (finalMessage) {
            if (finalMessage.answer && !['error', 'cancelled', 'permission_denied'].includes(finalMessage.status)) {
                setCompletedReplyId(pendingRequestId);
            }
            finishPendingRequest(pendingRequestId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliceCurrentId, sliceMessagesLoaded, sliceMessages, finishPendingRequest]);
    // assistantName читается из ref, чтобы useEffect ниже мог иметь deps=[]
    // и не перезапускал start/stop ChatAIHub при смене конфига во время pending request.
    const assistantNameRef = useRef(assistantName);
    useEffect(() => {
        assistantNameRef.current = assistantName;
    }, [assistantName]);

    // Effect 3: при смене режима на hero-экране пересобрать hello (текст и initial-questions
    // зависят от mode). Если уже отправлено сообщение — selector locked, mode не меняется,
    // эффект всё равно отработает безопасно (setMessages вернёт prev).
    // eslint-disable: buildHelloMessage пересоздаётся при изменении [assistantName, chatAIHelloMessage],
    // но оба производные от currentMode (через ConfigService-синглтон); добавлять buildHelloMessage
    // в deps вызвало бы лишний rebuild на ре-рендере без смены mode.
    useEffect(() => {
        if (!historyEnabled) return;
        setMessages((prev) => {
            if (prev.length === 1 && prev[0]?.id === HELLO_MESSAGE_ID) {
                return [buildHelloMessage()];
            }
            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMode]);

    const showHero = messages.length === 1 && messages[0]?.id === HELLO_MESSAGE_ID;
    const isModeSelectorLocked = sliceIsCurrentPersisted || messages.length > 1;
    const cancelDisabled = currentMode === 'agent';

    const handleModeChange = useCallback(
        (next: typeof currentMode) => {
            if (next === currentMode) return;
            // Пересоздаём draft-тред, чтобы старый conversationIdRef не «прилипал» к новому
            // mode (на бэке EnsureConversation в новом mode либо INSERT'нет новый row, либо
            // вернёт ModeMismatch на старый — ни того, ни другого не хотим).
            dispatch(setMode(next));
            dispatch(startNewConversation());
        },
        [currentMode, dispatch],
    );
    const heroSubtitle = (
        chatAIHelloMessage || 'Я ваш цифровой помощник. Задайте вопрос о системе — постараюсь помочь.'
    )
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/?p[^>]*>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    const focusInput = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    const handleClose = useCallback(() => {
        const stubEvent = {
            preventDefault: () => {},
            stopPropagation: () => {},
        } as unknown as React.MouseEvent;
        onClose(stubEvent);
    }, [onClose]);

    useEffect(() => {
        const signalrIdHandler = (id: string, data: IChatAIRAGResponse) => {
            if (id !== requestIdRef.current) {
                return;
            }

            // Бэк присылает status='Error' для сбоя генерации: такой ответ показываем
            // с пометкой, а не как обычное сообщение ассистента.
            const isErrorAnswer = (data.status ?? '').toLowerCase() === 'error';
            let text = isErrorAnswer
                ? data.message || data.answer
                : data.finishReason === 'NOT_FOUND'
                  ? 'По вашему запросу ничего не найдено.'
                  : data.answer;

            const links: string[] = [];
            data.additionalData?.forEach((item) => {
                const href = buildAttachmentDownloadHref(item.cardId, item.parentId, item.parentName);
                links.push(`- [${item.parentName}](${href})`);
                item.childLink?.forEach((subitem) => {
                    const childHref = buildAttachmentDownloadHref(item.cardId, subitem.childId, subitem.childName);
                    links.push(`  - [${subitem.childName}](${childHref})`);
                });
            });

            if (links.length > 0) {
                text = `${text}\n\n**Список документов:**\n${links.join('\n')}`;
            }

            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    message: stripAccessTokens(text),
                    direction: 'incoming',
                    sender: assistantNameRef.current,
                    ...nowStamp(),
                    id: id,
                    isError: isErrorAnswer,
                },
            ]);
            if (!isErrorAnswer) setCompletedReplyId(id);
            finishPendingRequest(id);
            // Ответ получен — выходим из группы, чтобы хаб не держал stale-requestId
            // в currentGroup (replay при reconnect должен трогать только pending).
        };

        const offMessage = chatAIHub.onMessageReceived(signalrIdHandler);
        void chatAIHub.start();

        return () => {
            offMessage();
            clearAgentStatusPolling();
            if (requestIdRef.current) {
                chatAIHub.leaveGroup(requestIdRef.current).catch(() => {});
            }
            requestIdRef.current = '';
            chatAIHub.stop();
        };
    }, [clearAgentStatusPolling, finishPendingRequest]);

    const handleSend = (text: string) => {
        if (isTyping) {
            return;
        }
        const trimmedMessage = text.replace(/[^\S\n]+/g, ' ').trim();
        if (!trimmedMessage) {
            return;
        }
        unsentMessage.current = '';

        const newMessage: ChatAIFormMessage = {
            message: trimmedMessage,
            direction: 'outgoing',
            sender: 'user',
            ...nowStamp(),
            id: uuidv4(),
        };

        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setSentCount((count) => count + 1);
        setIsTyping(true);

        // H1: дублируем pending-сообщение в slice, чтобы при переключении треда не потерять
        // вопрос пользователя. После ответа от бэка sidebar сделает refetch и реальные данные
        // придут через GET /conversations/{id} — но до этого момента нужный draft виден.
        if (historyEnabled && conversationIdRef.current) {
            const draftId = Date.now();
            pendingDraftIdRef.current = draftId;
            dispatch(addLocalMessage({
                conversationId: conversationIdRef.current,
                message: {
                    id: draftId,
                    requestId: '',
                    question: trimmedMessage,
                    answer: null,
                    status: 'pending',
                    createdAt: new Date().toISOString(),
                },
            }));
        }

        window.setTimeout(focusInput, 0);
    };

    const addErrorMessage = useCallback(
        (message: string) => {
            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    message,
                    direction: 'incoming',
                    sender: assistantName,
                    ...nowStamp(),
                    id: requestIdRef.current || `error-${Date.now()}`,
                    isError: true,
                },
            ]);
            setIsTyping(false);
            // Закрываем pending-черновик в slice со статусом error: иначе при переключении
            // треда (до того как backend запишет error в qa_log + придёт refetch) юзер
            // увидит «Ответ всё ещё формируется…» вместо самой ошибки.
            if (historyEnabled && conversationIdRef.current && pendingDraftIdRef.current != null) {
                dispatch(updateLocalMessage({
                    conversationId: conversationIdRef.current,
                    messageId: pendingDraftIdRef.current,
                    patch: { status: 'error', answer: message },
                }));
                pendingDraftIdRef.current = null;
            }
        },
        [assistantName, historyEnabled, dispatch],
    );

    const addIncomingMessage = useCallback((message: string, id?: string) => {
        const messageId = id || `message-${Date.now()}`;
        setCompletedReplyId(messageId);
        setMessages((prevMessages) => {
            if (prevMessages.some((item) => item.id === messageId)) {
                return prevMessages;
            }
            return [
                ...prevMessages,
                {
                    message,
                    direction: 'incoming',
                    sender: assistantNameRef.current,
                    ...nowStamp(),
                    id: messageId,
                },
            ];
        });
    }, []);

    const refreshCurrentConversationFromHistory = useCallback(
        async (requestId: string): Promise<boolean> => {
            const conversationId = conversationIdRef.current;
            if (!historyEnabled || !conversationId) {
                return false;
            }

            try {
                const { data } = await ChatAIHistoryService.getConversation(conversationId);
                dispatch(messagesLoadSuccess({
                    conversationId,
                    messages: data.messages,
                }));
                const answer = data.messages.find((m) => m.requestId === requestId);
                if (
                    requestIdRef.current === requestId &&
                    conversationIdRef.current === conversationId &&
                    answer?.answer &&
                    !['error', 'cancelled', 'permission_denied'].includes(answer.status)
                ) {
                    setCompletedReplyId(requestId);
                }
                return data.messages.some((m) => m.requestId === requestId && isConversationMessageFinal(m));
            } catch {
                return false;
            }
        },
        [dispatch, historyEnabled],
    );

    const startAgentStatusPolling = useCallback(
        (requestId: string) => {
            clearAgentStatusPolling();
            agentStatusPollStartedAtRef.current = Date.now();

            const scheduleNext = (poll: () => Promise<void>) => {
                if (requestIdRef.current !== requestId) {
                    return;
                }
                const elapsed = Date.now() - agentStatusPollStartedAtRef.current;
                if (elapsed >= AGENT_STATUS_POLL_MAX_MS) {
                    addErrorMessage('Не удалось дождаться финального статуса AI-агента. Повторите попытку позже.');
                    finishPendingRequest(requestId);
                    return;
                }
                agentStatusPollTimerRef.current = window.setTimeout(() => {
                    void poll();
                }, AGENT_STATUS_POLL_INTERVAL_MS);
            };

            const poll = async () => {
                if (requestIdRef.current !== requestId) {
                    return;
                }

                try {
                    const { data } = await ChatAIService.getAgentAnswerStatus(requestId);
                    if (requestIdRef.current !== requestId) {
                        return;
                    }

                    const status = data.status?.toLowerCase();
                    if (!isAgentFinalStatus(status)) {
                        scheduleNext(poll);
                        return;
                    }

                    const hasFinalHistoryMessage = await refreshCurrentConversationFromHistory(requestId);
                    if (requestIdRef.current !== requestId) {
                        return;
                    }

                    if (hasFinalHistoryMessage) {
                        finishPendingRequest(requestId);
                        return;
                    }

                    if (status === 'completed') {
                        if (historyEnabled) {
                            scheduleNext(poll);
                            return;
                        }
                        addIncomingMessage(
                            'Ответ сформирован, но не был доставлен в чат. Задайте вопрос ещё раз.',
                            requestId,
                        );
                    } else if (status === 'cancelled') {
                        addErrorMessage(data.error || 'Запрос отменён.');
                    } else if (status === 'approval_pending') {
                        addIncomingMessage(
                            data.error || 'Для продолжения требуется подтверждение действия.',
                            requestId,
                        );
                    } else {
                        addErrorMessage(data.error || 'Не удалось получить ответ AI-агента.');
                    }

                    finishPendingRequest(requestId);
                } catch {
                    scheduleNext(poll);
                }
            };

            agentStatusPollTimerRef.current = window.setTimeout(() => {
                void poll();
            }, AGENT_STATUS_POLL_INTERVAL_MS);
        },
        [
            addErrorMessage,
            addIncomingMessage,
            clearAgentStatusPolling,
            finishPendingRequest,
            historyEnabled,
            refreshCurrentConversationFromHistory,
        ],
    );

    const handleCancel = useCallback(() => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        clearAgentStatusPolling();

        const currentRequestId = requestIdRef.current;
        if (currentRequestId) {
            ChatAIService.cancelRequest(currentRequestId).catch(() => {});
            chatAIHub.leaveGroup(currentRequestId).catch(() => {});
        }

        requestIdRef.current = '';
        setIsTyping(false);
        // Аналогично error: закрываем draft статусом cancelled.
        if (historyEnabled && conversationIdRef.current && pendingDraftIdRef.current != null) {
            dispatch(updateLocalMessage({
                conversationId: conversationIdRef.current,
                messageId: pendingDraftIdRef.current,
                patch: { status: 'cancelled', answer: null },
            }));
            pendingDraftIdRef.current = null;
        }
    }, [historyEnabled, dispatch, clearAgentStatusPolling]);

    const addResultMessage = useCallback(
        (response: AxiosResponse<IChatAIAnswer>, userTitle: string) => {
            const requestId = response.data.requestId?.trim() || '';
            requestIdRef.current = requestId;

            if (response.data.error) {
                addErrorMessage(response.data.error);
                return;
            }

            if (!requestId) {
                addErrorMessage('Не удалось получить идентификатор запроса AI-сервиса. Повторите попытку позже.');
                return;
            }

            if (historyEnabled) {
                dispatch(markCurrentPersisted({ title: userTitle }));
            }

            pendingDraftIdRef.current = null;
            chatAIHub.joinGroup(requestId).catch(() => {});
            if (useAIAgent) {
                startAgentStatusPolling(requestId);
            }
        },
        [addErrorMessage, dispatch, historyEnabled, startAgentStatusPolling, useAIAgent],
    );

    const processMessages = useCallback(
        (chatMessages: ChatAIFormMessage[]) => {
            if (ENABLE_MOCK_BOT_RESPONSE) {
                const mockId = `mock-${Date.now()}`;
                requestIdRef.current = mockId;

                window.setTimeout(() => {
                    setMessages((prevMessages) => [
                        ...prevMessages,
                        {
                            message: MOCK_BOT_MARKDOWN_RESPONSE,
                            direction: 'incoming',
                            sender: assistantName,
                            ...nowStamp(),
                            id: mockId,
                        },
                    ]);
                    setIsTyping(false);
                }, 700);

                return;
            }

            const apiMessages = chatMessages
                .filter((x) => x.id != '-1')
                .map((messageObject) => {
                    const role = messageObject.sender === assistantName ? 'assistant' : 'user';
                    return {
                        role: role,
                        content: messageObject.message,
                        conversationId: conversationIdRef.current,
                    } as IChatAIQuestion;
                });

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            // Title из первого user-вопроса берём прямо здесь — у chatMessages актуальный
            // слепок. Не пытаемся достать его из messages closure внутри addResultMessage:
            // processMessages — useCallback без messages в deps, так что closure stale
            // (оставался [hello] при question-card-кликe → title слетал в 'Новый чат').
            const firstUserMessage = chatMessages.find((m) => m.direction === 'outgoing');
            const userTitle = (firstUserMessage?.message ?? '')
                .replace(/<[^>]+>/g, '')
                .replace(/\s+/g, ' ')
                .trim();

            if (useAIAgent) {
                ChatAIService.getAgentAnswer(apiMessages, abortController.signal)
                    .then((response) => addResultMessage(response, userTitle))
                    .catch((error) => {
                        if (axios.isCancel(error)) {
                            return;
                        }
                        addErrorMessage('Не удалось получить ответ AI-ассистента. Повторите попытку позже.');
                    });
            } else {
                ChatAIService.getAnswer(apiMessages, abortController.signal)
                    .then((response) => addResultMessage(response, userTitle))
                    .catch((error) => {
                        if (axios.isCancel(error)) {
                            return;
                        }
                        addErrorMessage('Не удалось получить ответ цифрового помощника. Повторите попытку позже.');
                    });
            }
        },
        [addErrorMessage, addResultMessage, assistantName, useAIAgent],
    );

    useEffect(() => {
        if (messages.length > 1 && messages[messages.length - 1].direction == 'outgoing') {
            processMessages(messages);
        }
    }, [messages, processMessages]);

    useEffect(() => {
        if (!isTyping && messages.length > 1) {
            window.setTimeout(focusInput, 0);
        }
    }, [isTyping, messages.length, focusInput]);

    useEffect(() => {
        focusInput();
        let elapsed = 0;
        const intervalId = window.setInterval(() => {
            elapsed += 30;
            const focused = inputRef.current?.hasFocus() ?? false;
            if (!focused) {
                focusInput();
            }
            if (elapsed >= 1500 || focused) {
                window.clearInterval(intervalId);
            }
        }, 30);
        return () => window.clearInterval(intervalId);
    }, [focusInput]);

    const feed = messages.filter((message) => message.id !== HELLO_MESSAGE_ID);

    const badge = STATUS_BADGE_TEXTS[status];
    const isOffline = status === 'offline';
    const isReconnecting = status === 'reconnecting';
    const sendBlocked = isTyping || isOffline || isReconnecting;
    const offlineHint = getOfflineHint(status, notConfigured);

    if (isMinimized) {
        return (
            <ChatAIMiniature
                assistantName={assistantName}
                isTyping={isTyping}
                completedReplyId={completedReplyId}
                status={status}
                onExpand={onExpand}
                onClose={handleClose}
            />
        );
    }

    return (
        <Modal
            className={`ai-form-modal${historyEnabled ? '' : ' ai-form-modal--no-sidebar'}`}
            width={1120}
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape
            noFrame
            onClose={handleClose}
            hasUnsavedInput={() => unsentMessage.current.trim() !== ''}
        >
            <div className="ai-form-modal__layout">
                {historyEnabled && <ChatAISidebar />}
                <div className="ai-form-modal__main">
                    <div className="ai-form-modal__header">
                        <div className="chat-ai-header">
                            {historyEnabled && (
                                <button
                                    type="button"
                                    className="chat-ai-header__sidebar-toggle"
                                    aria-label="Открыть историю"
                                    title="История"
                                    onClick={() => dispatch(toggleSidebarMobile())}
                                >
                                    <MdMenu size={18} />
                                </button>
                            )}
                            <div className="chat-ai-header__title">
                                <span className="chat-ai-header__name">{assistantName}</span>
                            </div>
                            {/* Селектор сам возвращает null, если из ragAvailable/agentAvailable
                                доступен только один режим — единая точка решения, не дублируем гейт. */}
                            {(chatAIEnabled || agentAIEnabled) && (
                                <ChatAIModeSelector
                                    mode={currentMode}
                                    onChange={handleModeChange}
                                    ragAvailable={chatAIEnabled}
                                    agentAvailable={agentAIEnabled}
                                    disabled={isModeSelectorLocked}
                                    disabledReason={
                                        sliceIsCurrentPersisted
                                            ? 'Режим зафиксирован — создайте новый чат для смены'
                                            : 'Сначала отправьте сообщение или создайте новый чат'
                                    }
                                />
                            )}
                            <span
                                className={`chat-ai-header__badge chat-ai-header__badge--${status}`}
                                aria-label={badge.a11y}
                                aria-live="polite"
                                title={badge.a11y}
                            >
                                <span className="chat-ai-header__badge-dot" aria-hidden />
                                {STATUS_MINI_TEXTS[status]}
                            </span>
                            <button
                                type="button"
                                className="chat-ai-header__minimize"
                                aria-label="Свернуть в мини-окно"
                                title="Свернуть"
                                onClick={onMinimize}
                            >
                                <TbPictureInPictureOn size={18} aria-hidden />
                            </button>
                            <button
                                type="button"
                                className="chat-ai-header__close"
                                aria-label="Закрыть"
                                title="Закрыть"
                                onClick={handleClose}
                            >
                                <MdClose size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="ai-form-modal__content">
                        <div className="ai-form-modal-body">
                            <div className="chat-ai-content">
                                {showHero ? (
                                    <div className="chat-ai-hero" role="region" aria-label="Начало диалога">
                                        <ChatAIAvatar />
                                        <div className="chat-ai-hero__heading">
                                            <h2 className="chat-ai-hero__title">Чем могу помочь?</h2>
                                            <p className="chat-ai-hero__subtitle">{heroSubtitle}</p>
                                        </div>
                                        {chatAIInitialQuestions.length > 0 && (
                                            <div
                                                className="chat-ai-hero__questions"
                                                aria-label="Примеры вопросов"
                                            >
                                                {chatAIInitialQuestions.map((question, index) => (
                                                    <button
                                                        key={question}
                                                        type="button"
                                                        className="chat-ai-question-card"
                                                        style={{ '--i': index } as React.CSSProperties}
                                                        disabled={sendBlocked}
                                                        onClick={() => {
                                                            void handleSend(question);
                                                        }}
                                                    >
                                                        <span
                                                            className="chat-ai-question-card__glyph"
                                                            aria-hidden
                                                        >
                                                            ↗
                                                        </span>
                                                        <span className="chat-ai-question-card__text">
                                                            {question}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <ChatAIMessageList key={sliceCurrentId ?? 'local'} bottomKey={sentCount}>
                                        {withDaySeparators(feed).map((item) => {
                                            if (item.kind === 'day') {
                                                return (
                                                    <ChatAIDaySeparator
                                                        key={item.key}
                                                        label={formatDaySeparator(item.sentAt)}
                                                    />
                                                );
                                            }
                                            return (
                                                <ChatAIFeedEntry
                                                    key={item.key}
                                                    message={item.message}
                                                    assistantName={assistantName}
                                                />
                                            );
                                        })}
                                    </ChatAIMessageList>
                                )}
                            </div>
                            <div className="chat-ai-input-area">
                                <div className="chat-ai-typing-bar" aria-live="polite">
                                    {isTyping && (
                                        <ChatAITypingStatus
                                            assistantName={assistantName}
                                            typingStatusMessages={typingStatusMessages}
                                        />
                                    )}
                                </div>
                                <div
                                    className={`chat-ai-input-shell${isTyping ? ' is-typing' : ''}${
                                        isOffline || isReconnecting ? ' is-offline' : ''
                                    }`}
                                >
                                    <ChatAIMessageInput
                                        ref={inputRef}
                                        label="Вопрос помощнику"
                                        placeholder={
                                            isOffline
                                                ? STATUS_OFFLINE_HINT
                                                : isReconnecting
                                                  ? STATUS_RECONNECTING_HINT
                                                  : 'Введите ваш вопрос'
                                        }
                                        sendDisabled={sendBlocked}
                                        defaultValue={unsentMessage.current}
                                        onSend={handleSend}
                                        onChange={(text) => {
                                            unsentMessage.current = text;
                                        }}
                                    />
                                    {isTyping && !cancelDisabled && (
                                        <button
                                            type="button"
                                            className="chat-ai-stop-button"
                                            aria-label="Остановить"
                                            onClick={() => {
                                                void handleCancel();
                                            }}
                                        >
                                            <span className="chat-ai-stop-button__icon" />
                                        </button>
                                    )}
                                </div>
                                {offlineHint && (
                                    <div
                                        className="chat-ai-offline-hint"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        {offlineHint}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>{/* /ai-form-modal__main */}
            </div>
            <div className="chat-ai-input-hint" aria-hidden>
                <span>
                    <kbd>Enter</kbd> — отправить
                </span>
                <span className="chat-ai-input-hint__sep">·</span>
                <span>
                    <kbd>Shift</kbd> + <kbd>Enter</kbd> — перенос строки
                </span>
                <span className="chat-ai-input-hint__sep">·</span>
                <span>
                    <kbd>Esc</kbd> — закрыть
                </span>
            </div>
        </Modal>
    );
};

export default ChatAIForm;
